import React from 'react';
import { FormattedMessage, IntlShape } from 'react-intl';
import {
    ArtifactStatus,
    ArtifactType,
    TRequestArtifactsTab,
    WebinarRecordingType,
    IArtifactMp4Options,
    ResolutionMp4,
    ReplaysOptions,
    TReplaysOptionsForBatchRequest,
    RecordingType,
    IArtifactMp4RecordingTypes
} from '../api/artifacts.dao';

export const artifactStyleByStatus: Record<ArtifactStatus, string> = {
    [ArtifactStatus.FAILED]: 'meeting-artifact-list-item__failed',
    [ArtifactStatus.READY]: 'meeting-artifact-list-item__ready',
    [ArtifactStatus.PENDING]: 'meeting-artifact-list-item__pending',
    [ArtifactStatus.NOT_AVAILABLE]: 'meeting-artifact-list-item__not-available',
    [ArtifactStatus.PUBLISHED]: 'meeting-artifact-list-item__ready',
    [ArtifactStatus.REVIEW_REQUIRED]: 'meeting-artifact-list-item__failed',
};

export const artifactStatusLabel: Record<ArtifactStatus, JSX.Element> = {
    [ArtifactStatus.FAILED]: <FormattedMessage
        id='oe.artifacts.status.failed'
        defaultMessage='Failed'
        description='Label text'
    />,
    [ArtifactStatus.READY]: <FormattedMessage
        id='oe.artifacts.status.ready'
        defaultMessage='Ready'
        description='Label text'
    />,
    [ArtifactStatus.PENDING]: <FormattedMessage
        id='oe.artifacts.status.pending'
        defaultMessage='Pending'
        description='Label text'
    />,
    [ArtifactStatus.NOT_AVAILABLE]: <FormattedMessage
        id='oe.artifacts.status.notAvailable'
        defaultMessage='Not Available'
        description='Label text'
    />,
    [ArtifactStatus.PUBLISHED]: <FormattedMessage
        id='oe.artifacts.status.published'
        defaultMessage='Published'
        description='Label text'
    />,
    [ArtifactStatus.REVIEW_REQUIRED]: <FormattedMessage
        id='oe.artifacts.status.reviewRequired'
        defaultMessage='Review Required'
        description='Label text'
    />,
};

export const passportReplayStatusLabel = (intl: IntlShape): Record<TReplaysOptionsForBatchRequest, string> => ({
    [ReplaysOptions.PUBLISH_AUTOMATICALLY]: intl.formatMessage({
        id: 'oe.artifacts.publishAutomatically',
        description: 'Radio button text',
        defaultMessage: 'Publish Automatically'
    }),
    [ReplaysOptions.REVIEW_REQUIRED]: intl.formatMessage({
        id: 'oe.artifacts.reviewRequired',
        description: 'Radio button text',
        defaultMessage: 'Review Required'
    }),
});

export const artifactTypeLabel: Record<ArtifactType, JSX.Element> = {
    [ArtifactType.TRANSCRIPT]: <FormattedMessage
        id='oe.artifacts.type.transcript'
        defaultMessage='Transcript'
        description='Label text'
    />,
    [ArtifactType.MP3]: <FormattedMessage
        id='oe.artifacts.type.mp3'
        defaultMessage='MP3'
        description='Label text'
    />,
    [ArtifactType.MP4_720P]: <FormattedMessage
        id='oe.artifacts.type.mp4_720_p'
        defaultMessage='MP4'
        description='Label text'
    />,
    [ArtifactType.MP4]: <FormattedMessage
        id='oe.artifacts.type.mp4'
        defaultMessage='MP4'
        description='Label text'
    />,
    [ArtifactType.PASSPORT_REPLAY]: <FormattedMessage
        id='oe.artifacts.type.passportReplay'
        defaultMessage='Passport Replay'
        description='Label text'
    />,
};

export const requestArtifactsTabLabels: Record<TRequestArtifactsTab, JSX.Element> = {
    artifacts: <FormattedMessage
        id='oe.requestArtifacts.tab.artifacts'
        defaultMessage='Artifacts'
        description='Tab label'
    />,
    replays: <FormattedMessage
        id='oe.requestArtifacts.tab.replays'
        defaultMessage='Passport Replays'
        description='Tab label'
    />,
};


export const requestArtifactsEmptyListMessage = (intl: IntlShape): Record<TRequestArtifactsTab, string> => ({
    artifacts: intl.formatMessage({
        description: 'Text',
        defaultMessage: 'There are no meetings for which artifacts are available.',
        id: 'oe.artifacts.noMeetingsForWhichArtifactsAreAvailable'
    }),
    replays: intl.formatMessage({
        description: 'Text',
        defaultMessage: 'There are no meetings for which passport replays are available.',
        id: 'oe.artifacts.noMeetingsForWhichPassportReplaysAreAvailable'
    }),
});

export const assetVideoType = [ArtifactType.MP4, ArtifactType.MP4_720P];

export const webinarRecordingTypeLabel: Record<WebinarRecordingType, JSX.Element> = {
    [WebinarRecordingType.SHARED_SCREEN_WITH_SPEAKER_VIEW_CC]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.shared_screen_with_speaker_view(CC)'
        defaultMessage='Shared Screen with Speaker View(CC)'
        description='Label text'
    />,
    [WebinarRecordingType.SHARED_SCREEN_WITH_SPEAKER_VIEW]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.shared_screen_with_speaker_view'
        defaultMessage='Shared Screen with Speaker View'
        description='Label text'
    />,
    [WebinarRecordingType.SHARED_SCREEN_WITH_GALLERY_VIEW]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.shared_screen_with_gallery_view'
        defaultMessage='Shared Screen with Gallery View'
        description='Label text'
    />,
    [WebinarRecordingType.ACTIVE_SPEAKER]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.active_speaker'
        defaultMessage='Active Speaker'
        description='Label text'
    />,
    [WebinarRecordingType.AUDIO_INTERPRETATION]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.audio_interpretation'
        defaultMessage='Audio Interpretation'
        description='Label text'
    />,
    [WebinarRecordingType.AUDIO_ONLY]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.audio_only'
        defaultMessage='Audio Only'
        description='Label text'
    />,
    [WebinarRecordingType.AUDIO_ONLY_EACH_PARTICIPANT]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.audio_only_each_participant'
        defaultMessage='Audio Only Each Participant'
        description='Label text'
    />,
    [WebinarRecordingType.AUDIO_TRANSCRIPT]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.audio_transcript'
        defaultMessage='Audio Transcript'
        description='Label text'
    />,
    [WebinarRecordingType.CC_TRANSCRIPT]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.cc_transcript'
        defaultMessage='CC Transcript'
        description='Label text'
    />,
    [WebinarRecordingType.CHAT_FILE]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.chat_file'
        defaultMessage='Chat File'
        description='Label text'
    />,
    [WebinarRecordingType.CLOSED_CAPTION]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.closed_caption'
        defaultMessage='Closed Caption'
        description='Label text'
    />,
    [WebinarRecordingType.GALLERY_VIEW]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.gallery_view'
        defaultMessage='Gallery View'
        description='Label text'
    />,
    [WebinarRecordingType.HOST_VIDEO]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.host_video'
        defaultMessage='Host Video'
        description='Label text'
    />,
    [WebinarRecordingType.POLL]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.poll'
        defaultMessage='Poll'
        description='Label text'
    />,
    [WebinarRecordingType.SHARED_SCREEN]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.shared_screen'
        defaultMessage='Shared Screen'
        description='Label text'
    />,
    [WebinarRecordingType.SUMMARY]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.summary'
        defaultMessage='Summary'
        description='Label text'
    />,
    [WebinarRecordingType.SUMMARY_NEXT_STEPS]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.summary_next_steps'
        defaultMessage='Summary Next Steps'
        description='Label text'
    />,
    [WebinarRecordingType.SIGN_INTERPRETATION]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.sign_interpretation'
        defaultMessage='Sign Interpretation'
        description='Label text'
    />,
    [WebinarRecordingType.THUMBNAIL]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.thumbnail'
        defaultMessage='Thumbnail'
        description='Label text'
    />,
    [WebinarRecordingType.TIMELINE]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.timeline'
        defaultMessage='Timeline'
        description='Label text'
    />,
    [WebinarRecordingType.SUMMARY_SMART_CHAPTERS]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.summary_smart_chapters'
        defaultMessage='Summary Smart Chapters'
        description='Label text'
    />,
    [WebinarRecordingType.SPEAKER_VIEW]: <FormattedMessage
        id='oe.artifacts.webinarRecordingTypeLabel.speaker_view'
        defaultMessage='Speaker View'
        description='Label text'
    />,
};

export const MAX_QUANTITY_OF_SELECTED_MEETINGS = 20;

export const artifactMp4Values = (intl: IntlShape): IArtifactMp4Options[] => [
    {
        value: ResolutionMp4.DEFAULT,
        label: intl.formatMessage({
            id: 'oe.common.default',
            description: 'Label text',
            defaultMessage: 'Default',
        })
    },
    {
        value: ResolutionMp4.MP4_360,
        label: intl.formatMessage({
            id: `oe.artifacts.type.360p`,
            description: 'Label text',
            defaultMessage: '360p',
        })
    }, {
        value: ResolutionMp4.MP4_720,
        label: intl.formatMessage({
            id: `oe.artifacts.type.720p`,
            description: 'Label text',
            defaultMessage: '720p',
        })
    }, {
        value: ResolutionMp4.MP4_1080,
        label: intl.formatMessage({
            id: `oe.artifacts.type.1080p`,
            description: 'Label text',
            defaultMessage: '1080p',
        })
    }
];

export const artifactMp4RecordingTypes = (intl: IntlShape): IArtifactMp4RecordingTypes[] => [
    {
        value: RecordingType.DEFAULT,
        label: intl.formatMessage({
            id: 'oe.common.default',
            description: 'Label text',
            defaultMessage: 'Default',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.common.default',
            description: 'Label text',
            defaultMessage: 'Default',
        }),
    },
    {
        value: RecordingType.SHARED_SCREEN_WITH_SPEAKER_VIEW_CC,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.sharedScreenWithSpeaker(ClosedCaptions)',
            description: 'Label text',
            defaultMessage: 'Shared Screen with Speaker (Closed Captions)',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.SS&Speaker(CC)',
            description: 'Label text',
            defaultMessage: 'SS & Speaker (CC)',
        }),
    },
    {
        value: RecordingType.SHARED_SCREEN_WITH_SPEAKER_VIEW,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.sharedScreenWithSpeaker',
            description: 'Label text',
            defaultMessage: 'Shared Screen with Speaker',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.SS&Speaker',
            description: 'Label text',
            defaultMessage: 'SS & Speaker',
        }),
    },
    {
        value: RecordingType.SHARED_SCREEN_WITH_GALLERY_VIEW,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.sharedScreenWithGallery',
            description: 'Label text',
            defaultMessage: 'Shared Screen with Gallery',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.SS&Speaker',
            description: 'Label text',
            defaultMessage: 'SS & Gallery',
        }),
    },
    {
        value: RecordingType.SPEAKER,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.speaker',
            description: 'Label text',
            defaultMessage: 'Speaker',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.speaker',
            description: 'Label text',
            defaultMessage: 'Speaker',
        }),
    },
    {
        value: RecordingType.GALLERY_VIEW,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.gallery',
            description: 'Label text',
            defaultMessage: 'Gallery',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.gallery',
            description: 'Label text',
            defaultMessage: 'Gallery',
        })
    },
    {
        value: RecordingType.SHARED_SCREEN,
        label: intl.formatMessage({
            id: 'oe.artifacts.recordingType.sharedScreen',
            description: 'Label text',
            defaultMessage: 'Shared Screen',
        }),
        textInTable: intl.formatMessage({
            id: 'oe.artifacts.recordingType.sharedScreen',
            description: 'Label text',
            defaultMessage: 'Shared Screen',
        })
    },
];
